import { FormEvent, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import api from '../lib/api'

function Section({ title, count, children }: { title: string; count: number; children: React.ReactNode }) {
  return (
    <div className="bg-white border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-semibold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-500">{count}</span>
      </div>
      {count === 0 ? <div className="text-sm text-gray-500">No matches.</div> : children}
    </div>
  )
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams()
  const initialQ = params.get('q') ?? ''
  const [input, setInput] = useState(initialQ)

  const query = useQuery({
    queryKey: ['global-search', initialQ],
    queryFn: () => api.search.global(initialQ).then((r) => r.data),
    enabled: initialQ.trim().length > 0,
  })

  const totals = useMemo(() => {
    const d = query.data
    if (!d) return 0
    return d.vehicles.length + d.orders.length + d.quotes.length + d.loads.length + d.invoices.length + d.customers.length + d.places.length + d.lanes.length + d.vinInventory.length + d.carriers.length + d.drivers.length
  }, [query.data])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const next = input.trim()
    if (!next) {
      setParams({})
      return
    }
    setParams({ q: next })
  }

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Global Search</h1>
        <p className="text-sm text-gray-500 mt-1">Find orders, vehicles, quotes, loads, invoices, customers, places, lanes, VIN inventory, carriers, and drivers.</p>
      </div>

      <form onSubmit={onSubmit} className="bg-white border rounded-xl p-4 flex gap-2">
        <input
          className="flex-1 border rounded px-3 py-2 text-sm"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search VIN, order #, quote #, load #, invoice #, customer, phone, email, reference"
        />
        <button type="submit" className="bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-blue-800">Search</button>
      </form>

      {!initialQ && <div className="text-sm text-gray-500 bg-white border rounded-xl p-6">Enter a query to search across all dispatch entities.</div>}
      {query.isLoading && <div className="text-sm text-gray-500 bg-white border rounded-xl p-6">Searching...</div>}
      {query.isError && <div className="text-sm text-red-600 bg-white border rounded-xl p-6">Search failed. Please try again.</div>}

      {!!initialQ && query.isSuccess && totals === 0 && (
        <div className="text-sm text-gray-500 bg-white border rounded-xl p-6">No matches found.</div>
      )}

      {query.isSuccess && totals > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Section title="Vehicles" count={query.data.vehicles.length}>
            <div className="space-y-2 text-sm">
              {query.data.vehicles.map((v) => (
                <div key={v.id} className="border rounded px-3 py-2">
                  <div className="font-medium">{v.vin}</div>
                  <div className="text-gray-600">{[v.year, v.make, v.model, v.type].filter(Boolean).join(' ') || '—'}</div>
                  <div className="text-xs text-gray-500">Status: {v.currentStatus ?? '—'} • Customer: {v.customerName ?? '—'}</div>
                  <div className="text-xs text-gray-500">Last 8: {v.vinLast8} • Last 6: {v.vinLast6}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Orders" count={query.data.orders.length}>
            <div className="space-y-2 text-sm">
              {query.data.orders.map((o) => (
                <div key={o.id} className="border rounded px-3 py-2">
                  <Link to={`/orders/${o.id}`} className="font-medium text-blue-700 hover:underline">{o.orderNumber}</Link>
                  <div className="text-gray-600">{o.customerName ?? '—'} • VINs {o.vinCount ?? 0}</div>
                  <div className="text-xs text-gray-500">{o.status} • Ready {o.readyDate || '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Quotes" count={query.data.quotes.length}>
            <div className="space-y-2 text-sm">
              {query.data.quotes.map((q) => (
                <div key={q.id} className="border rounded px-3 py-2">
                  <Link to={`/quotes/${q.id}`} className="font-medium text-blue-700 hover:underline">{q.quoteNumber}</Link>
                  <div className="text-gray-600">{q.customerName ?? '—'} • {q.vin ?? '—'}</div>
                  <div className="text-xs text-gray-500">{q.status}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Loads" count={query.data.loads.length}>
            <div className="space-y-2 text-sm">
              {query.data.loads.map((l) => (
                <div key={l.id} className="border rounded px-3 py-2">
                  <Link to={`/loads/${l.id}`} className="font-medium text-blue-700 hover:underline">{l.loadNumber ?? l.id}</Link>
                  <div className="text-gray-600">{l.origin} → {l.destination}</div>
                  <div className="text-xs text-gray-500">{l.status} • VINs {(l.vinCount ?? l.vehicles?.length ?? 0)}</div>
                  <div className="text-xs text-gray-500">Carrier: {l.assignedCarrierName ?? '—'} • Driver: {l.assignedDriverName ?? '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Invoices" count={query.data.invoices.length}>
            <div className="space-y-2 text-sm">
              {query.data.invoices.map((i) => (
                <div key={i.id} className="border rounded px-3 py-2">
                  <Link to={`/invoices/${i.id}`} className="font-medium text-blue-700 hover:underline">{i.invoice_number}</Link>
                  <div className="text-gray-600">Customer: {i.customer_name ?? '—'} • Status: {i.status}</div>
                  <div className="text-xs text-gray-500">Amount: {i.amount ?? i.net_amount ?? i.gross_amount ?? '—'} • Balance: {i.balance ?? '—'} {i.currency ?? ''}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Customers" count={query.data.customers.length}>
            <div className="space-y-2 text-sm">
              {query.data.customers.map((c) => (
                <div key={c.id} className="border rounded px-3 py-2">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-gray-600">{c.account_number}</div>
                  <div className="text-xs text-gray-500">{c.email ?? '—'} • {c.phone ?? '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Places" count={query.data.places.length}>
            <div className="space-y-2 text-sm">
              {query.data.places.map((p) => (
                <div key={p.id} className="border rounded px-3 py-2">
                  <div className="font-medium">{p.name ?? 'Place'}</div>
                  <div className="text-gray-600">{[p.address1, p.city, p.state, p.zip].filter(Boolean).join(', ') || '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Lanes" count={query.data.lanes.length}>
            <div className="space-y-2 text-sm">
              {query.data.lanes.map((lane) => (
                <div key={lane.id} className="border rounded px-3 py-2">
                  <Link to={`/lanes/${lane.id}`} className="font-medium text-blue-700 hover:underline">{lane.laneNumber}</Link>
                  <div className="text-gray-600">{lane.customerName ?? '—'}</div>
                  <div className="text-xs text-gray-500">
                    {[lane.originCity, lane.originState, lane.originZip].filter(Boolean).join(', ')}
                    {' -> '}
                    {[lane.destinationCity, lane.destinationState, lane.destinationZip].filter(Boolean).join(', ')}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="VIN Inventory" count={query.data.vinInventory.length}>
            <div className="space-y-2 text-sm">
              {query.data.vinInventory.map((item) => (
                <div key={item.id} className="border rounded px-3 py-2">
                  <Link
                    to={item.batchId ? `/vin-batches/${item.batchId}` : `/vin-inventory?q=${encodeURIComponent(item.vin)}`}
                    className="font-medium font-mono text-blue-700 hover:underline"
                  >
                    {item.vin}
                  </Link>
                  <div className="text-gray-600">{[item.year, item.make, item.model].filter(Boolean).join(' ') || '—'}</div>
                  <div className="text-xs text-gray-500">Batch: {item.batchNumber ?? '—'} • Customer: {item.customerName ?? '—'}</div>
                  <div className="text-xs text-gray-500">Status: {item.inventoryStatus} • Decode: {item.decodeStatus}</div>
                  <div className="mt-1 flex items-center gap-3 text-xs">
                    {item.batchId && (
                      <Link to={`/vin-batches/${item.batchId}`} className="text-blue-700 hover:underline">
                        Open batch
                      </Link>
                    )}
                    <Link to={`/vin-inventory?q=${encodeURIComponent(item.vin)}`} className="text-blue-700 hover:underline">
                      Open inventory
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Carriers" count={query.data.carriers.length}>
            <div className="space-y-2 text-sm">
              {query.data.carriers.map((c) => (
                <div key={c.id} className="border rounded px-3 py-2">
                  <div className="font-medium">{c.legal_name ?? c.dba_name ?? 'Carrier'}</div>
                  <div className="text-gray-600">{c.email ?? '—'} • {c.phone ?? '—'}</div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Drivers" count={query.data.drivers.length}>
            <div className="space-y-2 text-sm">
              {query.data.drivers.map((d) => (
                <div key={d.id} className="border rounded px-3 py-2">
                  <div className="font-medium">{[d.first_name, d.last_name].filter(Boolean).join(' ') || d.legal_name || 'Driver'}</div>
                  <div className="text-gray-600">{d.email ?? '—'} • {d.phone ?? '—'}</div>
                  <div className="text-xs text-gray-500">Status: {d.driver_status ?? '—'} • Unit: {d.unit_label ?? '—'}</div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      )}
    </div>
  )
}
